import { FC, useState } from 'react';
import { Globe, Check } from 'lucide-react';
import { useLanguage, Language } from '../../contexts/LanguageContext';

interface LanguageSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const LanguageSelectionModal: FC<LanguageSelectionModalProps> = ({ isOpen, onClose }) => {
  const { language: currentLanguage, setLanguage, t } = useLanguage();
  const [selectedLanguage, setSelectedLanguage] = useState<Language>(currentLanguage);

  const languages = [
    {
      code: 'en' as Language,
      name: 'English',
      nativeName: 'English'
    },
    {
      code: 'id' as Language,
      name: 'Indonesian',
      nativeName: 'Bahasa Indonesia'
    }
  ];

  const handleApply = () => {
    setLanguage(selectedLanguage);
    onClose();
  };

  const handleCancel = () => {
    setSelectedLanguage(currentLanguage);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="language-title"
      aria-describedby="language-description"
      data-modal="language-selection"
    >
      <div className="bg-card w-full max-w-md rounded-xl shadow-xl border border-border">
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
              <Globe className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 id="language-title" className="text-lg font-semibold text-card-foreground">
                {t('modals.language.title')}
              </h2>
              <p id="language-description" className="text-sm text-muted-foreground">
                {t('modals.language.subtitle')}
              </p>
            </div>
          </div>
        </div>

        {/* Language Options */}
        <div className="p-6 space-y-3">
          {languages.map((lang) => (
            <label
              key={lang.code}
              className="flex items-center space-x-3 p-4 rounded-lg border border-border hover:bg-accent/50 cursor-pointer transition-colors"
            >
              <input
                type="radio"
                name="language"
                value={lang.code}
                checked={selectedLanguage === lang.code}
                onChange={() => setSelectedLanguage(lang.code)}
                className="sr-only"
              />
              
              {/* Custom Radio Button */}
              <div className="flex-shrink-0">
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                  selectedLanguage === lang.code
                    ? 'border-primary bg-primary'
                    : 'border-muted-foreground'
                }`}>
                  {selectedLanguage === lang.code && (
                    <Check className="w-3 h-3 text-primary-foreground" />
                  )}
                </div>
              </div>

              {/* Language Info */}
              <div className="flex-1">
                <div className="font-medium text-card-foreground">
                  {lang.nativeName}
                </div>
                <div className="text-sm text-muted-foreground">
                  {lang.name}
                </div>
              </div>

              {/* Current Language Indicator */}
              {currentLanguage === lang.code && (
                <div className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full font-medium">
                  Current
                </div>
              )}
            </label>
          ))}
        </div>

        {/* Actions */}
        <div className="flex space-x-3 p-6 border-t border-border">
          <button
            onClick={handleCancel}
            className="flex-1 px-4 py-3 border border-border text-card-foreground rounded-lg font-medium hover:bg-muted/50 transition-colors"
            aria-label="Cancel language selection"
          >
            {t('modals.language.cancel')}
          </button>
          <button
            onClick={handleApply}
            className="flex-1 px-4 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors"
            aria-label="Apply selected language"
          >
            {t('modals.language.apply')}
          </button>
        </div>
      </div>
    </div>
  );
};